import { categorizeIndexValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon-white.svg';
import indexMapIcon from '../assets/index-map.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, PercentileData, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, fetchPercentileById } from "../utils/dashboardUtils";
import IconTooltip from "./IconTooltip";
import IndexIconTooltip from "./IndexIconTooltip";

interface IndexBannerProps {
    metric: DisaggregatedMetric | null;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const IndexBanner = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: IndexBannerProps) => {
    const laceyOrigin = "*";
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    const [percentile, setPercentile] = useState<PercentileData[] | null>(null);
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });
    const [indexEdgeProximity, setIndexEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);
    const indexTooltipRef = useRef<HTMLDivElement>(null);

    // Destructure metric properties
    // Provide default values if metric is null
    const id = metric?.id ?? 0;
    const name = metric?.name ?? "Selected Index";
    const versions = metric?.versions ?? [{ value: "-", unit: "-", percent_calc: null }];


    const val = versions[0].unit === "ratio" 
        ? `${versions[0].percent_calc?.n}/${versions[0].percent_calc?.d}` 
        : versions[0].value;

    // Ensure val is a number to pass to categorizeValue
    let numericVal: number;

    if (typeof val === 'string') {
        if (val.includes('/')) {
            // If val is a fraction (e.g., "5/10"), calculate the ratio
            const [numerator, denominator] = val.split('/').map(num => parseFloat(num.trim()));
            numericVal = denominator ? numerator / denominator : 0;
        } else {
            numericVal = parseFloat(val as string) || 0;
        }
    } else {
        numericVal = val;
    }

    // Use the categorizeValue function to get the category text
    const categoryText = percentile 
    ? categorizeIndexValue(numericVal, percentile, false) 
    : loading 
    ? "Loading..." 
    : "-";

    // Fetching county distribution and percentile data as soon as the component mounts
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const countyPromise = !countyDistributionData 
                    ? fetchDistributionData(id, [], null) 
                    : Promise.resolve(countyDistributionData);
                    
                const percentilePromise = !percentile 
                    ? fetchPercentileById(id) 
                    : Promise.resolve(percentile);
    
                const [countyResult, percentileResult] = await Promise.all([countyPromise, percentilePromise]);
    
                if (!countyDistributionData) setCountyDistributionData(countyResult || null);
                if (!percentile) setPercentile(percentileResult?.data || null);
            } catch (error) {
                console.error('Error fetching data:', error);
                setError(error as Error);
            } finally {
                setLoading(false);
            }
        };
    
        if (id && (!countyDistributionData || !percentile)) {
            fetchData();
        }
    }, [id, countyDistributionData, percentile]);

    const fetchData = async (fetchFunction: Function, setState: React.Dispatch<React.SetStateAction<any>>, dataState: any, id: number, geoPrefix?: string) => {
        if (dataState) return;  // If data is already fetched, return early

        setLoading(true);
        try {
            const result = await fetchFunction(id, geoPrefix);
            setState(result ? result.data : null);
        } catch (error) {
            setError(error as Error);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);
    
        // Fetch metadata, state data, and national data if not already fetched
        if (!metadata) await fetchData(fetchMetricMetadata, setMetadata, metadata, id);

        // Fetch raw distribution data if not already fetched
        if (!distributionData) {
            setLoading(true); // Set loading to true while fetching
            try {
                const result = await fetchIndicatorBinCount(id, geoDetails, geoTag); // Provide orgId, dashboardView, and geoTag
                setDistributionData(result || null); // Update state with fetched data
            } catch (error) {
                console.error('Error fetching distribution data:', error);
            } finally {
                setLoading(false); // Set loading to false after fetching
            }
        }
    };

    // handleClickIndexIcon - Handle click event for sending message contract to map application, including the index layer id and name to toggle an index map layer
    const handleClickIndexIcon = async () => {
        // Send message to the parent window
        window.parent.postMessage(
            {
                message: "toggleOverlayLayer",
                data: {
                    id,
                    name,
                },
            },
            laceyOrigin
        );
        console.log("Toggle index map layer message sent:", { id, name });
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 50,
                right: window.innerWidth - tooltipRect.right < 200,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }

        if (indexTooltipRef.current) {
            const indexTooltipRect = indexTooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: indexTooltipRect.top < 50,
                right: window.innerWidth - indexTooltipRect.right < 200,
                bottom: window.innerHeight - indexTooltipRect.bottom < 200,
                left: indexTooltipRect.left < 50
            };
            setIndexEdgeProximity(proximity);
        }
    }, []);

    return (
        <>
            <div className="flex items-center justify-between w-full h-12 bg-lacey-blue px-4">
                <div ref={indexTooltipRef} className="relative group">
                    <img src={indexMapIcon} onClick={handleClickIndexIcon} alt="Index icon" className="w-6 h-6 text-blue-500 hover:cursor-pointer" />
                    <IndexIconTooltip edgeProximity={indexEdgeProximity}/>
                </div>

                <p className="text-white text-sm font-light">The {name} is <strong>{categoryText}</strong></p>

                <div className="-mt-1">
                    {showMetadataIcon && 
                        <div ref={tooltipRef} className="relative group h-4 w-3">
                            <button onClick={handleClick}>
                                <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                            </button>
                            <IconTooltip edgeProximity={edgeProximity}/>
                        </div>
                    }
                </div>
            </div>
            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </>
    );
};


export default IndexBanner;
